/**
 * Der Rahmen, in dem die Collage steht — und wie die Reihe hineinpasst (Schritt 7,
 * ARCHITEKTUR.md 6.12).
 *
 * ⚠️ Diese Datei liegt in `shared/` und nicht im Browser, **obwohl der Export noch nicht
 * gebaut ist**. Der Grund steht in 6.12: „derselbe Renderer wie der Export, nur in kleiner
 * Auflösung". Eine Vorschau, die anders rechnet als der Export, zeigt etwas, das so nie
 * entsteht — und das fällt erst auf, wenn Anna die erste Vorlage ausdruckt. An einem Ort
 * abgelegt ist die Zusage prüfbar statt gut gemeint (2.5, dieselbe Begründung wie
 * `shared/anordnung.ts`).
 *
 * Alles hier ist rein: Zahlen rein, Zahlen raus, kein DOM. Die Ansicht steht in
 * `src/Rahmenvorschau.tsx`, das Zoomen in `src/lib/rahmen.ts`.
 */

/**
 * Die Rahmengröße. Aus Annas Mail vom 24.09.2026, 09:21 Uhr: „die finale Rahmengröße der
 * Collage beträgt 80 × 25 cm".
 *
 * ⚠️ **Der Widerspruch dazu ist nicht aufgelöst, nur überstimmt** (Carolin, 24.09.2026):
 * Annas Shop verkauft die Mini-Collage als „Format: A3 (Querformat)", also 42 × 29,7 cm.
 * Das sind zwei völlig verschiedene Bilder. Gehört ihr als Halbsatz bestätigt, **bevor**
 * ein Bildschirmfoto dieser Vorschau in ihre Unterlagen geht (`TODO.md`).
 */
export const RAHMEN_BREITE_CM = 80;
export const RAHMEN_HOEHE_CM = 25;

/** 3,2 : 1. Abgeleitet und nicht danebengeschrieben — zwei Zahlen, die dasselbe sagen,
 *  laufen beim nächsten Rahmenwechsel auseinander. */
export const RAHMEN_VERHAELTNIS = RAHMEN_BREITE_CM / RAHMEN_HOEHE_CM;

/**
 * Der weiße Rand ringsum, als Anteil der **langen** Kante. 6 % von 80 cm sind rund 4,8 cm.
 *
 * ⚠️ **Aus einer einzigen Aufnahme abgeleitet, und die zeigt ein anderes Format.** Auf
 * Annas Produktfoto füllen acht Figuren rund 89 % der Blattbreite und 76 % der Höhe —
 * aber dieses Foto zeigt ihre verkaufte Mini-Collage in A3 quer (1,41 : 1), nicht den
 * Rahmen 80 × 25 cm (3,2 : 1). Die Zahl ist ein begründeter Anfangswert, kein Ergebnis.
 * Sie gehört Anna vorgelegt, genau wie `UEBERLAPP_ANTEIL` in `src/lib/reihe.ts`
 * (`TODO.md`).
 *
 * *Warum überhaupt ein Rand:* Auf Annas eigenem Blatt steht die Reihe nicht bis zur
 * Papierkante. Ein Rahmen ohne Rand zeigte eine Collage, die es so nicht gibt — und er
 * verspräche mehr Platz für Figuren, als tatsächlich da ist.
 */
export const RAND_ANTEIL = 0.06;

/** Wo die Reihe im Rahmen liegt. Alle Werte in Pixeln des **gezeichneten** Rahmens. */
export interface Einpassung {
  /** Faktor, mit dem die Reihe aus `reihenlayout()` skaliert wird. */
  faktor: number;
  /** Höhe des Rahmens, aus seiner Breite und `RAHMEN_VERHAELTNIS`. */
  rahmenHoehe: number;
  /** Linke Kante der skalierten Reihe, von der linken Rahmenkante aus. */
  links: number;
  /** Die Bodenlinie: Abstand von der **unteren** Rahmenkante. */
  unten: number;
  innenBreite: number;
  innenHoehe: number;
}

/**
 * Die Reihe in den Rahmen einpassen — ganz hinein, senkrecht und waagerecht mittig.
 *
 * Gerechnet wird **contain**, also der kleinere der beiden Faktoren. Das ist kein Detail,
 * sondern der Grund, warum die Vorschau überhaupt etwas beantwortet:
 *
 * | Fall | Was bindet | Was man sieht |
 * |---|---|---|
 * | 60 Figuren | die Breite | kleine Figuren, viel weißes Papier darüber und darunter |
 * | 15 Figuren | die Höhe | Figuren über die volle Rahmenhöhe, die Reihe steht mittig |
 *
 * Nähme man stattdessen den größeren Faktor, liefe die Reihe über den Rahmen hinaus — und
 * die Vorschau zeigte gerade nicht mehr, was auf das Blatt passt.
 *
 * **Mittig und nicht auf einer Bodenlinie unten** (Carolin, 24.09.2026): auf Annas
 * Produktfoto liegt der weiße Rand oben **und** unten. Der Preis ist, dass der
 * Bezugspunkt aus dem Editor — die durchgezogene Bodenlinie am unteren Rand — in der
 * Vorschau woanders liegt.
 */
export function einpassen(
  reiheBreite: number,
  reiheHoehe: number,
  rahmenBreite: number,
  randAnteil: number = RAND_ANTEIL,
): Einpassung {
  if (!Number.isFinite(rahmenBreite) || rahmenBreite <= 0) {
    return { faktor: 0, rahmenHoehe: 0, links: 0, unten: 0, innenBreite: 0, innenHoehe: 0 };
  }

  const rahmenHoehe = rahmenBreite / RAHMEN_VERHAELTNIS;
  const rand = rahmenBreite * (Number.isFinite(randAnteil) ? Math.max(0, randAnteil) : 0);
  const innenBreite = Math.max(0, rahmenBreite - 2 * rand);
  const innenHoehe = Math.max(0, rahmenHoehe - 2 * rand);

  const brauchbar =
    Number.isFinite(reiheBreite) &&
    reiheBreite > 0 &&
    Number.isFinite(reiheHoehe) &&
    reiheHoehe > 0;
  // Eine leere Reihe bekommt Faktor 0 statt einer Rechnung mit `Infinity`. Gezeichnet
  // wird dann nichts — der Rahmen steht trotzdem, und das ist richtig: ein leeres Blatt
  // ist eine ehrliche Vorschau von null Figuren.
  const faktor = brauchbar ? Math.min(innenBreite / reiheBreite, innenHoehe / reiheHoehe) : 0;
  // ⚠️ Nicht `reiheBreite * faktor`: bei `reiheBreite = NaN` ist das trotz `faktor = 0`
  // wieder `NaN`, und ein `NaN` in `left` lässt den Browser die ganze Regel verwerfen —
  // die Reihe stünde dann still in der linken oberen Ecke statt im Rahmen.
  const skalBreite = brauchbar ? reiheBreite * faktor : 0;
  const skalHoehe = brauchbar ? reiheHoehe * faktor : 0;

  return {
    faktor,
    rahmenHoehe,
    links: rand + (innenBreite - skalBreite) / 2,
    unten: rand + (innenHoehe - skalHoehe) / 2,
    innenBreite,
    innenHoehe,
  };
}

/**
 * Eine Länge aus der Reihe in Zentimeter auf dem Blatt.
 *
 * Das ist die Zahl, die `TODO.md` offen führt — „Wie viele Figuren trägt 80 × 25 cm
 * wirklich?". Sie steht deshalb in der Vorschau und nicht in einer Notiz: sie entsteht
 * dort, und sie ändert sich mit jeder Figur, die dazukommt.
 */
export function inCm(laengePx: number, faktor: number, rahmenBreite: number): number {
  if (!Number.isFinite(laengePx) || !Number.isFinite(faktor)) return 0;
  if (!Number.isFinite(rahmenBreite) || rahmenBreite <= 0) return 0;
  return ((laengePx * faktor) / rahmenBreite) * RAHMEN_BREITE_CM;
}

/** Wie das Format vor der Kundin heißt. An einem Ort, damit es nicht in drei
 *  Schreibweisen durch die Oberfläche geht. */
export const RAHMEN_TEXT = `${String(RAHMEN_BREITE_CM)} × ${String(RAHMEN_HOEHE_CM)} cm`;
